import React, { useRef, useState } from 'react'

import ReactCountryFlag from 'react-country-flag'
import { useTranslation } from 'react-i18next'

import { RadioButton } from 'components-ref/atoms'
import { useOutsideAlerter } from 'helpers/hooks'
import { Icons } from 'helpers/icons'

import './_dropdownSelect.scss'

interface DropdownSelectProps {
    icon?: string
    botIcon?: string
    placeHolder: string
    hasBottomSection?: boolean
    searchTerm: string
    setSearchTerm: (_val: string) => void
    botSearchTerm?: string
    setBotSearchTerm?: (_val: string) => void
    elementsArr: any[]
    botElementsArr?: any[]
    topSectionSelected: any
    topSectionSelect: any
    botSectionSelected?: any
    botSectionSelect?: any
    customClass?: string
}

const DropdownSelect = ({
    icon,
    botIcon,
    placeHolder,
    hasBottomSection,
    searchTerm,
    setSearchTerm,
    elementsArr,
    topSectionSelected,
    topSectionSelect,
    botSearchTerm,
    setBotSearchTerm,
    botElementsArr,
    botSectionSelected,
    botSectionSelect,
    customClass = ''
}: DropdownSelectProps) => {
    const [dropdownActive, setDropdownActive] = useState<boolean>(false)
    const [altDropdownActive, setAltDropdownActive] = useState<boolean>(false)
    const { t } = useTranslation()
    const dropdownRef = useRef<HTMLDivElement>(null)

    useOutsideAlerter(dropdownRef, () => {
        setDropdownActive(false)
    })

    const renderTopElements = () => {
        return elementsArr?.map((item: any, key: number) => (
            <div
                key={key}
                className='dropdownSelect__element'
                onClick={() => {
                    topSectionSelect(item)
                    setDropdownActive(false)
                }}
            >
                <div className='dropdownSelect__element__icon'>
                    <div className='flag__wrapper'>
                        <ReactCountryFlag
                            countryCode={item.flag_code || item.code}
                            svg
                            cdnUrl='https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/4x3/'
                            cdnSuffix='svg'
                            title={item.flag_code || item.code}
                            style={{
                                width: '4em',
                                height: '3em'
                            }}
                        />
                    </div>
                </div>

                <div className='dropdownSelect__element__name'>{item.name}</div>

                <RadioButton active={topSectionSelected?.code === item.code} />
            </div>
        ))
    }

    const renderBotElements = () => {
        return botElementsArr?.map((item: any, key: number) => (
            <div
                key={key}
                className='dropdownSelect__element'
                onClick={() => {
                    botSectionSelect(item)
                    setAltDropdownActive(false)
                }}
            >
                <div className='dropdownSelect__element__icon' />
                <div className='dropdownSelect__element__name'>{item.name}</div>
                <RadioButton active={botSectionSelected === item} />
            </div>
        ))
    }

    return (
        <>
            <div
                ref={dropdownRef}
                className={`dropdownSelect${
                    botElementsArr?.length ? ' dropdownSelect--top' : ''
                } ${customClass}`}
            >
                <div
                    className='dropdownSelect__head'
                    onClick={() => {
                        setDropdownActive(!dropdownActive)
                    }}
                >
                    <div className='dropdownSelect__head__icon'>
                        {topSectionSelected?.code || topSectionSelected?.flag_code ? (
                            <div className='flag__wrapper'>
                                <ReactCountryFlag
                                    countryCode={
                                        topSectionSelected.flag_code ||
                                        topSectionSelected.code
                                    }
                                    svg
                                    cdnUrl='https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/4x3/'
                                    cdnSuffix='svg'
                                    title={
                                        topSectionSelected.flag_code ||
                                        topSectionSelected.code
                                    }
                                    style={{
                                        width: '4em',
                                        height: '3em'
                                    }}
                                />
                            </div>
                        ) : icon ? (
                            <img src={icon} alt={t('dropdownIcon')} />
                        ) : null}
                    </div>

                    <div className='dropdownSelect__head__text'>
                        {topSectionSelected && !dropdownActive ? (
                            <p className='dropdownSelect__head__text__title'>
                                {t('country')}
                            </p>
                        ) : (
                            ''
                        )}

                        <input
                            type='text'
                            placeholder={placeHolder}
                            value={
                                !topSectionSelected
                                    ? searchTerm
                                    : topSectionSelected.name
                            }
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                                topSectionSelect('')
                                setSearchTerm(e.currentTarget.value)
                            }}
                            onClick={(e: React.MouseEvent<HTMLElement>) => {
                                e.stopPropagation()
                                if (!dropdownActive) setDropdownActive(true)
                            }}
                        />
                    </div>

                    <div
                        className={`dropdownSelect__head__arrow dropdownSelect__head__arrow--${
                            dropdownActive ? 'active' : 'inactive'
                        }`}
                    >
                        <img src={Icons.chevronRight} alt={t('toggleArrow')} />
                    </div>
                </div>

                <div
                    className={`dropdownSelect__body dropdownSelect__body--${
                        dropdownActive ? 'active' : 'inactive'
                    }`}
                >
                    {renderTopElements()}
                </div>
            </div>

            {hasBottomSection ? (
                <div className='dropdownSelect dropdownSelect--bot'>
                    <div
                        className='dropdownSelect__head'
                        onClick={() => {
                            setAltDropdownActive(!altDropdownActive)
                        }}
                    >
                        {icon ? (
                            <div className='dropdownSelect__head__icon'>
                                <img src={botIcon} alt={t('dropdownIcon')} />
                            </div>
                        ) : (
                            ''
                        )}

                        <div className='dropdownSelect__head__text'>
                            {botSectionSelected && !altDropdownActive ? (
                                <p className='dropdownSelect__head__text__title'>
                                    {t('stateProvince')}
                                </p>
                            ) : (
                                ''
                            )}

                            <input
                                type='text'
                                placeholder={t('selectStateProvince')}
                                value={
                                    !botSectionSelected
                                        ? botSearchTerm
                                        : botSectionSelected.name
                                }
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                    botSectionSelect('')
                                    setBotSearchTerm?.(e.currentTarget.value)
                                }}
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                    e.stopPropagation()
                                    if (!altDropdownActive) {
                                        setAltDropdownActive(true)
                                    }
                                }}
                            />
                        </div>

                        <div
                            className={`dropdownSelect__head__arrow dropdownSelect__head__arrow--${
                                altDropdownActive ? 'active' : 'inactive'
                            }`}
                        >
                            <img
                                src={Icons.chevronRight}
                                alt={t('toggleArrow')}
                            />
                        </div>
                    </div>

                    <div
                        className={`dropdownSelect__body dropdownSelect__body--${
                            altDropdownActive ? 'active' : 'inactive'
                        }`}
                    >
                        {renderBotElements()}
                    </div>
                </div>
            ) : (
                ''
            )}
        </>
    )
}

export default DropdownSelect